import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    IconButton,
    CircularProgress,
    ListItemSecondaryAction,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Divider,
    Paper,
    Badge,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCode,
    faSignOutAlt,
    faUser,
    faPlus,
    faEdit,
    faTrash,
    faClock,
    faLayerGroup,
    faChevronLeft,
    faChevronRight,
    faShare,
    faUsers,
    faLink,
} from '@fortawesome/free-solid-svg-icons';
import apiService from '../services/api';
import { HtmlTemplate, TemplatesResponse, UploadFormData, UserRole } from '../types';
import { UploadModal } from './UploadModal';
import { ShareModal } from './ShareModal';
import { UsersListModal } from './UsersListModal';
import { UsersManagementModal } from './UsersManagementModal';
import { format } from 'date-fns';

const DRAWER_WIDTH = 360;
const COLLAPSED_DRAWER_WIDTH = 72;

interface DashboardProps {
    onLogout: () => void;
    authToken?: string;
    user?: {
        name: string;
        email: string;
        userId?: number;
    };
}

export const Dashboard: React.FC<DashboardProps> = ({ onLogout, authToken, user }) => {
    const [templates, setTemplates] = useState<HtmlTemplate[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<HtmlTemplate | null>(null);
    const [loading, setLoading] = useState(true);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<HtmlTemplate | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<HtmlTemplate | null>(null);
    const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
    const [actionLoading, setActionLoading] = useState<{
        delete?: number;
        edit?: number;
    }>({});
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isUsersListModalOpen, setIsUsersListModalOpen] = useState(false);
    const [isUsersManagementModalOpen, setIsUsersManagementModalOpen] = useState(false);
    const hasInitialFetch = useRef(false);

    const fetchTemplates = async () => {
        // Ensure the API service has the current token
        if (authToken) {
            apiService.setToken(authToken);
        }
        
        try {
            const response = await apiService.getTemplates();
            setTemplates(response.data.templates);
        } catch (error) {
            console.error('Error fetching templates:', error);
            showSnackbar('Failed to fetch templates', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Only fetch templates if user is authenticated and token is available
        // and we haven't already made the initial fetch
        if (authToken && !hasInitialFetch.current) {
            hasInitialFetch.current = true;
            fetchTemplates();
        }
    }, [authToken]);

    const handleUpload = async (formData: UploadFormData) => {
        try {
            await apiService.createTemplate(formData);
            fetchTemplates();
            setIsUploadModalOpen(false);
            showSnackbar('Template created successfully', 'success');
        } catch (error) {
            console.error('Error uploading template:', error);
            showSnackbar('Failed to create template', 'error');
        }
    };

    const handleEdit = async (formData: UploadFormData) => {
        if (!editingTemplate) return;
        setActionLoading(prev => ({ ...prev, edit: editingTemplate.id }));
        try {
            await apiService.updateTemplate(editingTemplate.id, formData);
            fetchTemplates();
            setEditingTemplate(null);
            showSnackbar('Template updated successfully', 'success');
        } catch (error) {
            console.error('Error updating template:', error);
            showSnackbar('Failed to update template', 'error');
        } finally {
            setActionLoading(prev => ({ ...prev, edit: undefined }));
        }
    };

    const handleDelete = async () => {
        if (!templateToDelete) return;
        setActionLoading(prev => ({ ...prev, delete: templateToDelete.id }));
        try {
            await apiService.deleteTemplate(templateToDelete.id);
            if (selectedTemplate?.id === templateToDelete.id) {
                setSelectedTemplate(null);
            }
            fetchTemplates();
            setDeleteConfirmOpen(false);
            setTemplateToDelete(null);
            showSnackbar('Template deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting template:', error);
            showSnackbar('Failed to delete template', 'error');
        } finally {
            setActionLoading(prev => ({ ...prev, delete: undefined }));
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCopyPublicLink = async () => {
        if (!selectedTemplate?.publicUrl) return;
        
        try {
            await navigator.clipboard.writeText(selectedTemplate.publicUrl);
            showSnackbar('Public link copied to clipboard!', 'success');
        } catch (error) {
            console.error('Failed to copy link:', error);
            showSnackbar('Failed to copy link', 'error');
        }
    };

    const handleShareTemplate = async (users: { emailId: string; role: UserRole }[]) => {
        if (!selectedTemplate) return;
        
        try {
            const response = await apiService.addUsersToTemplate(selectedTemplate.id, users);
            
            if (response.data.invalidEmails && response.data.invalidEmails.length > 0) {
                showSnackbar(
                    `Shared successfully! Invalid emails: ${response.data.invalidEmails.join(', ')}`,
                    'success'
                );
            } else {
                showSnackbar('Template shared successfully!', 'success');
            }
        } catch (error) {
            console.error('Error sharing template:', error);
            showSnackbar('Failed to share template', 'error');
            throw error; // Re-throw to let the modal handle the error
        }
    };

    // Check if current user is an admin (userIds 1, 2, 3)
    const isAdmin = () => {
        return user?.userId && [1, 2, 3].includes(user.userId);
    };

    return (
        <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'background.default' }}>
            <AppBar 
                position="fixed" 
                elevation={0}
                sx={{ 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    background: 'linear-gradient(90deg, primary.main 0%, primary.dark 100%)',
                }}
            >
                <Toolbar sx={{ minHeight: 64 }}>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            background: 'linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%)',
                            py: 0.75,
                            px: 2,
                            borderRadius: 2,
                            height: 40,
                        }}
                    >
                        <img 
                            src="https://hsasecurity.net/wp-content/uploads/2020/04/hsa_website_logo.png"
                            alt="HSA Security"
                            style={{
                                height: '100%',
                                width: 'auto',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                            }}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    {user && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
                            {isAdmin() && (
                                <Tooltip title="User Management">
                                    <Button
                                        onClick={() => setIsUsersManagementModalOpen(true)}
                                        startIcon={<FontAwesomeIcon icon={faUsers} />}
                                        sx={{ 
                                            color: 'white',
                                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                                            backdropFilter: 'blur(8px)',
                                            '&:hover': {
                                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                            },
                                            px: 2,
                                            py: 1,
                                            borderRadius: 2,
                                        }}
                                    >
                                        Users
                                    </Button>
                                </Tooltip>
                            )}
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                    Welcome, {user.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                    {user.email}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                }}
                            >
                                <FontAwesomeIcon icon={faUser} />
                            </Box>
                        </Box>
                    )}
                    <Button
                        onClick={onLogout}
                        startIcon={<FontAwesomeIcon icon={faSignOutAlt} />}
                        sx={{ 
                            color: 'white',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(8px)',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                            },
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                        }}
                    >
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            <Drawer
                variant="permanent"
                sx={{
                    width: isDrawerCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
                    flexShrink: 0,
                    transition: 'width 0.2s ease-in-out',
                    '& .MuiDrawer-paper': {
                        width: isDrawerCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
                        transition: 'width 0.2s ease-in-out',
                        overflow: 'hidden',
                    },
                }}
            >
                <Toolbar />
                <Box 
                    sx={{ 
                        position: 'relative',
                        height: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <IconButton
                        onClick={() => setIsDrawerCollapsed(!isDrawerCollapsed)}
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 12,
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '8px 0 0 8px',
                            borderRight: 'none',
                            '&:hover': {
                                bgcolor: 'primary.light',
                            },
                            width: 24,
                            height: 32,
                            '& svg': {
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                            },
                        }}
                    >
                        <FontAwesomeIcon icon={isDrawerCollapsed ? faChevronRight : faChevronLeft} />
                    </IconButton>

                    <Box sx={{ p: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={!isDrawerCollapsed && <FontAwesomeIcon icon={faPlus} />}
                            onClick={() => setIsUploadModalOpen(true)}
                            size="large"
                            sx={{
                                py: 1.5,
                                bgcolor: 'primary.main',
                                minWidth: 0,
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                },
                                ...(isDrawerCollapsed && {
                                    '& .MuiButton-startIcon': {
                                        margin: 0,
                                    },
                                }),
                            }}
                        >
                            {isDrawerCollapsed ? (
                                <FontAwesomeIcon icon={faPlus} />
                            ) : (
                                'Add HTML/Text'
                            )}
                        </Button>
                    </Box>

                    <Box 
                        sx={{ 
                            px: 2, 
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <FontAwesomeIcon 
                            icon={faLayerGroup} 
                            style={{ color: 'text.secondary' }} 
                        />
                        {!isDrawerCollapsed && (
                            <Typography 
                                variant="subtitle2" 
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                            >
                                Templates ({templates.length})
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ overflow: 'auto', flex: 1, px: 1 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <List>
                                {templates.map((template) => (
                                    <ListItem
                                        key={template.id}
                                        disablePadding
                                        sx={{
                                            position: 'relative',
                                            pr: !isDrawerCollapsed ? '90px' : 0, // Space for action buttons
                                        }}
                                        secondaryAction={
                                            !isDrawerCollapsed && template.userRole === "manager" && (
                                                <Box 
                                                    sx={{ 
                                                        display: 'flex', 
                                                        gap: 1,
                                                        position: 'absolute',
                                                        right: 8,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        bgcolor: 'background.paper',
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingTemplate(template);
                                                            }}
                                                            disabled={!!actionLoading.edit}
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&:hover': {
                                                                    color: 'primary.main',
                                                                    bgcolor: 'primary.light',
                                                                },
                                                            }}
                                                        >
                                                            {actionLoading.edit === template.id ? (
                                                                <CircularProgress size={18} />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faEdit} size="sm" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setTemplateToDelete(template);
                                                                setDeleteConfirmOpen(true);
                                                            }}
                                                            disabled={!!actionLoading.delete}
                                                            sx={{
                                                                color: 'text.secondary',
                                                                '&:hover': {
                                                                    color: 'error.main',
                                                                    bgcolor: 'error.light',
                                                                },
                                                            }}
                                                        >
                                                            {actionLoading.delete === template.id ? (
                                                                <CircularProgress size={18} />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faTrash} size="sm" />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )
                                        }
                                    >
                                        <ListItemButton
                                            selected={selectedTemplate?.id === template.id}
                                            onClick={() => setSelectedTemplate(template)}
                                            sx={{
                                                borderRadius: 2,
                                                mb: 0.5,
                                                py: 1.5,
                                                width: '100%',
                                                '&.Mui-selected': {
                                                    bgcolor: 'primary.light',
                                                    '&:hover': {
                                                        bgcolor: 'primary.light',
                                                    },
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Tooltip title={template.label}>
                                                            <Typography 
                                                                variant="subtitle2"
                                                                sx={{ 
                                                                    fontWeight: selectedTemplate?.id === template.id ? 600 : 400,
                                                                    color: selectedTemplate?.id === template.id ? 'primary.dark' : 'text.primary',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                {template.label}
                                                            </Typography>
                                                        </Tooltip>
                                                    </Box>
                                                }
                                                sx={{
                                                    m: 0,
                                                    '& .MuiListItemText-primary': {
                                                        width: '100%',
                                                    },
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
            </Drawer>

            <Box 
                component="main" 
                sx={{ 
                    flexGrow: 1, 
                    p: 3,
                    width: `calc(100% - ${isDrawerCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH}px)`,
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    height: '100vh',
                }}
            >
                <Toolbar />
                {selectedTemplate ? (
                    <Box sx={{ height: 'calc(100vh - 100px)' }}>
                        <Paper
                            sx={{
                                p: 2,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {selectedTemplate.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Role: {selectedTemplate.userRole}
                                </Typography>
                            </Box>
                            <Box sx={{ flexGrow: 1 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Last updated {format(new Date(selectedTemplate.updated_at), 'MMMM d, yyyy')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                {selectedTemplate.publicUrl && (
                                    <Tooltip title="Copy public link">
                                        <IconButton
                                            onClick={handleCopyPublicLink}
                                            sx={{
                                                color: 'primary.main',
                                                '&:hover': {
                                                    bgcolor: 'primary.light',
                                                },
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faLink} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {selectedTemplate.userRole === "manager" && (
                                    <>
                                        <Tooltip title="Share with users">
                                            <IconButton
                                                onClick={() => setIsShareModalOpen(true)}
                                                sx={{
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        bgcolor: 'primary.light',
                                                    },
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faShare} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View users with access">
                                            <IconButton
                                                onClick={() => setIsUsersListModalOpen(true)}
                                                sx={{
                                                    color: 'primary.main',
                                                    '&:hover': {
                                                        bgcolor: 'primary.light',
                                                    },
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faUsers} />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </Box>
                        </Paper>
                        <Paper
                            sx={{
                                height: 'calc(100% - 80px)',
                                overflow: 'hidden',
                                borderRadius: 2,
                            }}
                        >
                            <Box
                                component="iframe"
                                srcDoc={selectedTemplate.html_content}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none',
                                }}
                            />
                        </Paper>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            height: 'calc(100vh - 100px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: 'text.secondary',
                        }}
                    >
                        <FontAwesomeIcon 
                            icon={faCode} 
                            style={{ 
                                fontSize: '3rem',
                                marginBottom: '1rem',
                                opacity: 0.5,
                            }} 
                        />
                        <Typography sx={{ opacity: 0.7 }}>
                            Select an HTML/Text template from the sidebar to preview it here
                        </Typography>
                    </Box>
                )}
            </Box>

            <UploadModal
                open={isUploadModalOpen || editingTemplate !== null}
                onClose={() => {
                    setIsUploadModalOpen(false);
                    setEditingTemplate(null);
                }}
                onSubmit={editingTemplate ? handleEdit : handleUpload}
                initialData={editingTemplate ? {
                    label: editingTemplate.label,
                    html_content: editingTemplate.html_content,
                } : undefined}
            />

            <ShareModal
                open={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                onSubmit={handleShareTemplate}
                templateName={selectedTemplate?.label || ''}
            />

            <UsersListModal
                open={isUsersListModalOpen}
                onClose={() => setIsUsersListModalOpen(false)}
                templateId={selectedTemplate?.id || 0}
                templateName={selectedTemplate?.label || ''}
                currentUserEmail={user?.email}
            />

            <UsersManagementModal
                open={isUsersManagementModalOpen}
                onClose={() => setIsUsersManagementModalOpen(false)}
                currentUserId={user?.userId || 0}
            />

            <Dialog 
                open={deleteConfirmOpen} 
                onClose={() => setDeleteConfirmOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>Delete Template</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the HTML/Text template "{templateToDelete?.label}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                    <Button 
                        onClick={() => setDeleteConfirmOpen(false)} 
                        disabled={!!actionLoading.delete}
                        sx={{ color: 'text.secondary' }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDelete} 
                        color="error" 
                        variant="contained"
                        disabled={!!actionLoading.delete}
                        startIcon={actionLoading.delete ? <CircularProgress size={20} /> : undefined}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    severity={snackbar.severity} 
                    sx={{ 
                        width: '100%',
                        boxShadow: 2,
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}; 